import { ActivatedChallengeEventType, AppEvent, BasePayload, CreateMessageRequest, EmailCaptureEventType, EventTargetType, FormEventType, GetMessageResponse, MESSAGE_TEMPLATES, MessagePayload, MessageRecipientGroup, MessageRecipientGroupType, MessageTemplateFunction, RunType, UpdateMessageRequest } from '../../types';
import { GoogleTagEvent, del, get, logWithAmplitude, logWithGoogleTagAndTopicOrganization, post, put, throwHttpError } from './api';

import challengeData from '../state/challenge-data';
import { getMainPage } from '../page-name';
import { isDefined } from '../utils';
import pointCache from '../state/point-cache';
import topicData from '../state/topic-data';

export async function sendMapLinkViaSMS(
	topic_id: string,
	map_link: string,
	phone_number: string,
	organization_id: string,
) {
	const qs = new URLSearchParams();
	qs.append('map_link', map_link);
	qs.append('phone_number', phone_number);
	return await fetch(`/api/messages/${organization_id}/text_map_link?${qs.toString()}`, post({})).then(async response => {
		if (!response.ok) {
			// if the response is too many requests, throw a specific error
			if (response.status === 429) {
				getMainPage().errorToastElement.showToast('You have reached the maximum number of SMS requests allowed. Please try again later.');
				return await throwHttpError('Rate limit exceeded for sending map link via SMS.', response);
			}
			return await throwHttpError('An error occurred while sending the map link via SMS.', response);
		}

		logWithGoogleTagAndTopicOrganization('Send to Phone Via SMS', GoogleTagEvent.MAP_SHARE_CLICKED, {
			topic_id,
		});
	});
}

export async function getOrganizationMessages(
    entity_type: EventTargetType,
    entity_id: string,
    organization_id: string,
	topic_key?: string,
) {
    const response = await fetch(`/api/messages/${organization_id}?organization_key=${topic_key ?? ''}`, get());
    if (!response.ok) {
        await throwHttpError('An error occurred while getting organization messages.', response);
    }
    const messages: GetMessageResponse[] = await response.json();
    // filter messages by entity type and id
    const filteredMessages = messages.filter(message =>
        message.entity_type === entity_type && message.entity_id.$oid === entity_id
    );

    return filteredMessages;
}

export async function sendOrScheduleOrganizationMessage(
	amplitude_callsite: string,
	organization_id: string,
	message_details: CreateMessageRequest,
	organization_key?: string,
) {
	const response = await fetch(`/api/messages/${organization_id}?organization_key=${organization_key ?? ''}`, post(message_details));
	if (!response.ok) {
		await throwHttpError('An error occurred while setting the organization messages.', response);
	}

	logWithAmplitude(amplitude_callsite, 'Send Challenge Event Mail', {
		challenge_id: message_details.entity_id.$oid,
		organization_key,
		message_details
	});
}

export async function sendOrganizationMessagePreview(
	amplitude_callsite: string,
	organization_id: string,
	message_details: CreateMessageRequest,
	phone_number?: string,
	organization_key?: string,
) {
	const qs = new URLSearchParams();
	if (phone_number) {
		qs.append('phone_number', phone_number);
	}
	if (organization_key) {
		qs.append('organization_key', organization_key);
	}
	const response = await fetch(`/api/messages/${organization_id}/preview?${qs.toString()}`, post(message_details));
	if (!response.ok) {
		await throwHttpError('An error occurred while sending the challenge event mail preview.', response);
	}

	logWithAmplitude(amplitude_callsite, 'Send Challenge Event Mail Preview', {
		challenge_id: message_details.entity_id.$oid,
		organization_key,
		message_details
	});
}

export async function updateOrganizationMessage(
	amplitude_callsite: string,
	organization_id: string,
	message_id: string,
	message_details: UpdateMessageRequest,
	organization_key?: string,
) {
	const response = await fetch(`/api/messages/${organization_id}?message_id=${message_id}&organization_key=${organization_key ?? ''}`, put(message_details));
	if (!response.ok) {
		await throwHttpError('An error occurred while updating the organization message.', response);
	}

	logWithAmplitude(amplitude_callsite, 'Update Organization Message', {
		message_id,
		organization_key,
		message_details
	});
}

export async function deleteOrganizationMessage(
	amplitude_callsite: string,
	organization_id: string,
	message_id: string,
	organization_key?: string,
) {
	const response = await fetch(`/api/messages/${organization_id}?message_id=${message_id}&organization_key=${organization_key ?? ''}`, del());
	if (!response.ok) {
		await throwHttpError('An error occurred while deleting the organization message.', response);
	}

	logWithAmplitude(amplitude_callsite, 'Delete Organization Message', {
		message_id,
		organization_key
	});
}

function createSentMessageTemplate(date: Date): string {

    return `This message was sent on ${formatDate(date)} at ${formatTime(date)}.`;
}

function formatDate(date: Date): string {
    return date.toLocaleDateString([], { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function formatTime(date: Date): string {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function applyTemplate(template: string | MessageTemplateFunction<any>, params: any): string {
	if (typeof template === 'function') {
	  return template(params);
	}

	// Replace template placeholders with values
	return Object.entries(params).reduce((result, [key, value]) => {
	  return result.replace(new RegExp(`{${key}}`, 'g'), String(value));
	}, template);
}

export function getTemplateFromMessage(message: CreateMessageRequest | GetMessageResponse | undefined, message_target_type: EventTargetType) {
	const currentGroupType = getRecipientGroupType(message?.recipient_group);
	const currentEventType = eventTypeFromMessageRunAt(message);
	switch (messageRunAtValue(message)) {
		case 'scheduled':
		case 'immediate':
	        return MESSAGE_TEMPLATES[message_target_type].scheduled.find(
				(e) => currentGroupType === e.recipient_group
			);

		case 'automated':
        return MESSAGE_TEMPLATES[message_target_type].automated.find(
            (e) => currentGroupType === e.recipient_group && currentEventType === e.event
		);
	}
}

export function eventTypeFromMessageRunAt(message: CreateMessageRequest | GetMessageResponse | undefined): ActivatedChallengeEventType | FormEventType | AppEvent | EmailCaptureEventType | '' {
	if (!isDefined(message) || !isDefined(message.run_at)) return defaultEvent(message);
	if (typeof message.run_at === 'string') return defaultEvent(message);
	if ('OnEvent' in message.run_at) {
		const eventType = getEventType(message.run_at.OnEvent);
		return eventType !== '' ? eventType : defaultEvent(message);
	}
	return defaultEvent(message);
}

export function getEventType(event: AppEvent | undefined): ActivatedChallengeEventType | FormEventType | EmailCaptureEventType | '' {
	if (!event || typeof event !== 'object') return '';
	if ('ActivatedChallenge' in event) return event.ActivatedChallenge;
	if ('Form' in event) return event.Form;
	if ('EmailCapture' in event) return event.EmailCapture;
	return '';
}

export function getAppEvent(event: string | undefined): AppEvent {
  if (!event) return { ActivatedChallenge: ActivatedChallengeEventType.Registration };
  if (Object.values(ActivatedChallengeEventType).includes(event as ActivatedChallengeEventType)) {
    return { ActivatedChallenge: event as ActivatedChallengeEventType };
  }
  if (Object.values(FormEventType).includes(event as FormEventType)) {
    return { Form: event as FormEventType };
  }

  if (Object.values(EmailCaptureEventType).includes(event as EmailCaptureEventType)) {
    return { EmailCapture: event as EmailCaptureEventType };
  }
  return { ActivatedChallenge: ActivatedChallengeEventType.Registration };
}

export function defaultEvent(message: CreateMessageRequest | GetMessageResponse | undefined): AppEvent  {
	switch (message?.entity_type) {
		case EventTargetType.ActivatedChallenge:
			return {ActivatedChallenge: ActivatedChallengeEventType.Registration};
		case EventTargetType.Form:
			return {Form: FormEventType.SubmissionCreated};
		case EventTargetType.EmailCapture:
			return {EmailCapture: EmailCaptureEventType.PopUpCapture};
	}

	return {ActivatedChallenge: ActivatedChallengeEventType.Registration};
}

export function getRecipientGroupType(recipientGroup: MessageRecipientGroup | undefined): MessageRecipientGroupType | undefined {
	if (!isDefined(recipientGroup)) return undefined;
	if (typeof recipientGroup === 'string') {
		// Convert display string to enum value if needed
		const map: Record<string, MessageRecipientGroupType> = {
			'ActivatedChallengeParticipants': MessageRecipientGroupType.ActivatedChallengeParticipants,
			'ActivatedChallengeLocationVisitors': MessageRecipientGroupType.ActivatedChallengeLocationVisitors,
			'ActivatedChallengeLocationSavers': MessageRecipientGroupType.ActivatedChallengeLocationSavers,
			'ActivatedChallengeRewardRecipients': MessageRecipientGroupType.ActivatedChallengeRewardRecipients,
			'ActivatedChallengeActivityCompleters': MessageRecipientGroupType.ActivatedChallengeActivityCompleters,
			'CheckinCountZero': MessageRecipientGroupType.CheckinCountZero,
			'SaveCountZero': MessageRecipientGroupType.SaveCountZero,
			'FormSubmissionOwners': MessageRecipientGroupType.FormSubmissionOwners,
			'FormSubmissionEmailField': MessageRecipientGroupType.FormSubmissionEmailField
		};
		return map[recipientGroup] ?? (recipientGroup as MessageRecipientGroupType);
	}

	// Check for known keys in the object
	const keys = [
		'ActivatedChallengeLocationVisitors',
		'ActivatedChallengeLocationSavers',
		'ActivatedChallengeRewardRecipients',
		'ActivatedChallengeActivityCompleters',
		'CheckinCountFewerThan',
		'CheckinCountMoreThan',
		'SaveCountFewerThan',
		'SaveCountMoreThan',
		'FormSubmissionOwners',
		'FormSubmissionEmailField'
	] as const;

	for (const key of keys) {
		if (key in recipientGroup) {
			// Always return the enum value
			return MessageRecipientGroupType[key as keyof typeof MessageRecipientGroupType];
		}
	}
	return undefined;
}


export function messagePlainTextDescription(message: CreateMessageRequest | GetMessageResponse | undefined): string {
	if (!isDefined(message)) return '';

	// Handle already sent messages
	if (isGetMessageResponse(message) && message.sent_at) {
	  const sentDate = new Date(message.sent_at);
	  return createSentMessageTemplate(sentDate);
	}

	// Get templates for this message type
	const template = getTemplateFromMessage(message, message.entity_type);

	if (!template) {
		return 'No Description Available';
	}

	// For automated messages
	if (messageRunAtValue(message) === 'automated') {
	  // Get params for template based on message type
	  const params = {
		locationName: getLocationNameFromPayload(message),
		rewardName: getRewardTitleFromPayload(message),
		pointName: topicData.topic?.point_name,
		includeAdmin: getIncludeAdminFromPayload(message),
		customFieldName: getCustomFieldNameFromPayload(message)
	  };

	  return `This message will automatically send ${applyTemplate(template?.recipientDescription, params)}`;
	}

	// For scheduled/immediate messages
	const timingDescription = messageRunAtValue(message) === 'scheduled'
	  ? `on ${messageScheduledAtDate(message)} at ${messageScheduledAtTime(message)}`
	  : 'immediately';

	// Get recipient description with params
	const params = {
		locationName: getLocationNameFromPayload(message),
		rewardName: getRewardTitleFromPayload(message),
		pointName: topicData.topic?.point_name,
		includeAdmin: getIncludeAdminFromPayload(message),
		customFieldName: getCustomFieldNameFromPayload(message),
	};

	const recipientDescription = applyTemplate(template?.recipientDescription, params);

	return `This message will be sent ${timingDescription} to ${recipientDescription}.`;
}

export function getNestedOid(obj: unknown, ...path: string[]): string {
	let current: any = obj;
	for (const key of path) {
		if (!isDefined(current) || typeof current !== 'object') return '';
		current = current[key];
	}
	// Handles both string and {$oid: string} cases
	if (typeof current === 'string') return current;
	if (isDefined(current) && typeof current.$oid === 'string') return current.$oid;
	return '';
}

function getIncludeAdminFromPayload(message: CreateMessageRequest | GetMessageResponse): boolean {
	if ('Form' in message?.payload) {
		return message?.payload.Form.include_map_admins;
	}
	return false;
}

function getLocationNameFromPayload(message: CreateMessageRequest | GetMessageResponse): string {
    const locationId = getNestedOid(message?.recipient_group, 'ActivatedChallengeLocationVisitors', 'challenge_location_id', '$oid') ?? getNestedOid(message?.recipient_group, 'ActivatedChallengeLocationSavers', 'point_id', '$oid');

    const location = challengeData.challengeLocations.find(loc =>
        loc.challenge_location_info?._id?.$oid === locationId || loc.point_info?._id?.$oid === locationId
    );

    return location?.point_info?.name ?? 'the selected location';
}

function getRewardTitleFromPayload(message: CreateMessageRequest | GetMessageResponse): string {
    const rewardId = getNestedOid(message?.recipient_group, 'ActivatedChallengeRewardRecipients', 'reward_id', '$oid');

    const reward = challengeData.challenge?.rewards?.find(r =>
        r._id?.$oid === rewardId
    );

    return reward?.title ?? 'the selected reward';
}

function getCustomFieldNameFromPayload(message: CreateMessageRequest | GetMessageResponse): string {
    const customFieldId = getNestedOid(message?.recipient_group, 'FormSubmissionEmailField', 'custom_field_id', '$oid');

    const customField = topicData.topic?.add_point_customization?.custom_fields?.find(field =>
		field._id?.$oid === customFieldId
    );

    return customField?.customizations.header ?? 'that is selected';
}

export function messageRunAtValue(message: CreateMessageRequest | GetMessageResponse | undefined): 'scheduled' | 'automated' | 'immediate' {
	if (message?.run_at === 'Immediate') {
		return 'immediate';
	} else if (typeof message?.run_at === 'object' && 'Scheduled' in message.run_at) {
		return 'scheduled';
	} else if (typeof message?.run_at === 'object' && 'OnEvent' in message.run_at) {
		return 'automated';
	} else {
		return 'immediate'; // Default to immediate if not specified
	}
}

export function runAtEvent(message_run_at: RunType | undefined): AppEvent | undefined {
	if (!isDefined(message_run_at)) {
        return undefined;
    }
	if ((typeof message_run_at === 'object' && 'Scheduled' in message_run_at) || message_run_at === 'Immediate') {
		return undefined;
	} else if ('OnEvent' in message_run_at && typeof message_run_at === 'object') {
		return message_run_at.OnEvent;
	} else {
		return undefined;
	}
}

function isGetMessageResponse(message: CreateMessageRequest | GetMessageResponse): message is GetMessageResponse {
	return 'sent_at' in message;
}


export function messageRunAtTime(message: CreateMessageRequest | GetMessageResponse | undefined): Date {
	if (messageRunAtValue(message) === 'scheduled' && typeof message?.run_at === 'object' && 'Scheduled' in message.run_at) {
		return new Date(message.run_at.Scheduled);
	}
	return new Date(); // Default to current time if not scheduled
}

export function messageScheduledAtDate(message: CreateMessageRequest): string {
	if (typeof message.run_at === 'object' && 'Scheduled' in message.run_at) {
		return `${new Date(message.run_at.Scheduled).toLocaleDateString([], { year: 'numeric', month: '2-digit', day: '2-digit' })}`;
	}
	return '';
}

export function messageScheduledAtTime(message: CreateMessageRequest): string {
	if (typeof message.run_at === 'object' && 'Scheduled' in message.run_at) {
		return `${new Date(message.run_at.Scheduled).toLocaleTimeString([], { hour:
		'2-digit', minute: '2-digit' })}`;
	}
	return '';
}

export function getMessageHtml(type: EventTargetType, message: MessagePayload | undefined, logo: string, cover_image: string) : string {
	if (!isDefined(message)) {
		return '';
	}
    const background_color = topicData.backgroundColor;
    const text_color = topicData.textColor;
    // Extract the payload data based on message type
    let payload: BasePayload;
    if ('ActivatedChallenge' in message) {
        payload = message.ActivatedChallenge;
    } else if ('Form' in message) {
        payload = message.Form;
    } else {
        // Fallback for unexpected message types
        payload = {
            subject: '',
            body: '',
            accent_color: background_color,
            button_text_color: text_color,
			reply_to: undefined,
        };
    }

    const body = payload.body ?? '';

    return(applyPersonalizationsToHTML(type, get_default_html(logo, cover_image, body, background_color, text_color, payload)));
}

function applyPersonalizationsToHTML(type: EventTargetType, html:string) : string {
	switch (type) {
			case EventTargetType.ActivatedChallenge: {
				const first_activated_challenge = challengeData.activated_challenges[0] ?? {display_name: '', email: '', link_set: {}};
				html = html.replace('{{{display_name}}}', first_activated_challenge.display_name ?? '');
				html = html.replace('{{{email}}}', first_activated_challenge.email ?? '');
				html = html.replace('{{{secret_activated_challenge_link}}}', first_activated_challenge.link_set?.activated_challenge_page_write_link ?? '');
				break;
			}

			case EventTargetType.Form: {
				const first_point = pointCache.points[0] ?? {name: '', description: '', location: new URL('https://'), write_link: ''};
				html = html.replace('{{{point_name}}}', first_point.name ?? '');
				html = html.replace('{{{point_description}}}', first_point.description ?? '');
				html = html.replace('{{{point_location}}}', first_point.location.search ?? '');
				html = html.replace('{{{secret_edit_point_url}}}', first_point.write_link ?? '');
				break;
			}

			// Add other cases as needed
		}
	return html;
}

function get_default_html(logo: string, cover_image: string, body: string, background_color: string, text_color: string, payload: BasePayload): string {
    // Build action buttons HTML for preview
    // In the actual sent email, the server populates action_buttons with real personalized links.
    // For preview, we show a placeholder button when include_unique_link is enabled.
    const includeLink = (payload as any).include_unique_link === true;
    const buttonsHtml = includeLink
        ? `<a href="#" class="email-btn" style="display: inline-block; background-color: ${background_color}; color: ${text_color}; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; font-size: 14px; font-weight: 600; text-decoration: none; padding: 13px 32px; border-radius: 8px; margin: 4px 0; line-height: 1.2;">Your Unique Link</a>`
        : '';

    const replyToHtml = payload.reply_to
        ? `<tr><td align="center" class="email-reply" style="padding: 0 48px 28px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; font-size: 12px; color: #9ca3af;">Need support? Contact <a href="mailto:${payload.reply_to}" style="color: #6b7280; text-decoration: underline;">${payload.reply_to}</a></td></tr>`
        : '';

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <!--[if mso]>
  <xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml>
  <style>
    table { border-collapse: collapse; }
    sup, sub { font-size: 100% !important; }
    sup { mso-text-raise: 10% }
    sub { mso-text-raise: -10% }
  </style>
  <![endif]-->
  <style>
    body, table, td, a { -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
    body, table, td { margin: 0; padding: 0; }
    img { -ms-interpolation-mode: bicubic; border: 0; outline: none; text-decoration: none; }
    a[x-apple-data-detectors] { color: inherit !important; text-decoration: inherit !important; }
    #MessageViewBody a { color: inherit; text-decoration: none; }
    @media only screen and (max-width: 620px) {
      .email-container { width: 100% !important; max-width: 100% !important; }
      .email-card { border-radius: 0 !important; }
      .email-padding { padding-left: 16px !important; padding-right: 16px !important; }
      .email-padding-wide { padding-left: 20px !important; padding-right: 20px !important; }
      .email-body { padding: 28px 20px 16px !important; font-size: 15px !important; }
      .email-btn-wrap { padding: 4px 20px 24px !important; }
      .email-btn { display: block !important; width: 100% !important; text-align: center !important; box-sizing: border-box !important; padding: 14px 20px !important; }
      .email-cover { padding: 0 16px 20px !important; }
      .email-cover img { border-radius: 8px !important; }
      .email-reply { padding: 0 20px 20px !important; }
      .email-outer-pad { padding: 16px 8px !important; }
    }
  </style>
</head>
<body style="margin: 0; padding: 0; background-color: ${background_color}; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">

  <!-- Outer wrapper -->
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: ${background_color};">
    <tr>
      <td align="center" class="email-outer-pad" style="padding: 32px 16px;">

        <!-- Logo -->
        ${logo ? `
        <table role="presentation" class="email-container" width="560" cellpadding="0" cellspacing="0" border="0">
          <tr>
            <td align="center" style="padding-bottom: 20px;">
              <img src="${logo}" alt="Logo" width="100" style="display: block; border-radius: 8px; height: auto; max-width: 100px;">
            </td>
          </tr>
        </table>
        ` : ''}

        <!-- White card -->
        <table role="presentation" class="email-container email-card" width="560" cellpadding="0" cellspacing="0" border="0" style="background-color: #ffffff; border-radius: 16px;">

          <!-- Body text -->
          <tr>
            <td class="email-body" style="padding: 40px 48px 24px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; font-size: 15px; line-height: 1.7; color: #374151;">
              ${body}
            </td>
          </tr>

          <!-- Action buttons -->
          ${buttonsHtml ? `
          <tr>
            <td align="center" class="email-btn-wrap" style="padding: 8px 48px 32px;">
              ${buttonsHtml}
            </td>
          </tr>
          ` : ''}

          <!-- Cover image -->
          ${cover_image ? `
          <tr>
            <td class="email-cover" style="padding: 0 24px 28px;">
              <img src="${cover_image}" alt="" width="512" style="display: block; width: 100%; max-width: 512px; height: 180px; object-fit: cover; border-radius: 12px;">
            </td>
          </tr>
          ` : ''}

          <!-- Reply-to support -->
          ${replyToHtml}

        </table>
        <!-- End white card -->

      </td>
    </tr>
  </table>

</body>
</html>`;
}